import type { AdvancedThreshold } from "@/overlay/components/CommonParts/Threshold/types";
import { Aggregate } from "@mm/api.meteomatics.com";
import type { CustomGeoJSONCustomOptions } from "@mm/metx-workbench.meteomatics.com";
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, LineString, Polygon } from "geojson";
import type { DateTime } from "luxon";

export const constructHashId = (
  userFileId?: number | null,
  dateTime?: DateTime,
  model?: string | null,
  parameter?: string | null,
  customOption?: CustomGeoJSONCustomOptions,
  advancedThresholds?: AdvancedThreshold[],
  hasBasicThresholds?: boolean | null,
  backendHydrateMode?: boolean | null,
): string => {
  const fileIdPart = userFileId || "no_userFileId";

  const advancedThresholdsHash = (advancedThresholds ?? [])
    .flatMap((t) => t.details ?? [])
    .filter((d) => d?.model)
    .map(({ parameter, model, offsetMinutes, id }) => `${parameter}-${model}-${offsetMinutes}-${id}`)
    .join("|");

  // Without thresholds there is no time-varying hydration state, so collapse the render-cache key
  // to a file-scoped entry and let the static geometry cache handle reuse.
  const hasAdvanced = advancedThresholdsHash.length > 0;
  const hasDynamicHydration = hasAdvanced || !!hasBasicThresholds || !!customOption?.show_values;

  const dateTimePart = dateTime && hasDynamicHydration ? dateTime.toMillis() : "none";
  const modelPart = hasDynamicHydration ? model || "none" : "none";
  const parameterPart = hasDynamicHydration ? parameter || "none" : "none";
  const unitsPart = hasDynamicHydration ? customOption?.units || "none" : "none";
  const segmentLengthPart = hasDynamicHydration ? customOption?.segmentLength || "none" : "none";
  const aggregatePart = hasDynamicHydration ? customOption?.aggregate_value || "none" : "none";
  const showValuesPart = hasDynamicHydration
    ? customOption?.show_values
      ? "show_values_on"
      : "show_values_off"
    : "show_values_off";

  // If advanced thresholds are active, ignore basic thresholds in the cache key
  // to avoid refetching on basic-threshold changes.
  const basicThresholdsPart = !hasAdvanced && hasBasicThresholds ? "basic_thr_on" : "";

  const hydrationModePart = !hasDynamicHydration
    ? "hydr_none"
    : backendHydrateMode == null
      ? "hydr_none"
      : backendHydrateMode
        ? "hydr_backend"
        : "hydr_frontend";

  return [
    fileIdPart,
    dateTimePart,
    modelPart,
    parameterPart,
    unitsPart,
    segmentLengthPart,
    aggregatePart,
    showValuesPart,
    basicThresholdsPart,
    advancedThresholdsHash || "none",
    hydrationModePart,
  ].join("-");
};

export const constructPolygonSeriesChunkHashId = (args: {
  userFileId: number;
  animationWindowKey: string;
  chunkIndex: number;
  chunkStartMs: number;
  temporalResolutionMs: number;
  chunkFrameCount: number;
  model?: string | null;
  parameter?: string | null;
  customOption?: CustomGeoJSONCustomOptions;
  advancedThresholds?: AdvancedThreshold[];
  hasBasicThresholds?: boolean | null;
}): string => {
  const advancedThresholdsHash = (args.advancedThresholds ?? [])
    .flatMap((t) => t.details ?? [])
    .filter((d) => d?.model && d?.parameter)
    .map(
      ({ parameter, model, offsetMinutes, id, aggregate }) =>
        `${parameter}-${model}-${offsetMinutes}-${id}-${aggregate || "none"}`,
    )
    .join("|");

  const hasAdvanced = advancedThresholdsHash.length > 0;
  const basicThresholdsPart = !hasAdvanced && args.hasBasicThresholds ? "basic_thr_on" : "";

  return [
    "polygon-series",
    args.userFileId,
    args.animationWindowKey,
    `chunk-${args.chunkIndex}`,
    args.chunkStartMs,
    args.temporalResolutionMs,
    args.chunkFrameCount,
    args.model || "none",
    args.parameter || "none",
    args.customOption?.units || "none",
    args.customOption?.segmentLength || "none",
    args.customOption?.aggregate_value || "none",
    basicThresholdsPart,
    advancedThresholdsHash || "none",
  ].join("-");
};

export const filterFulfilledPromises = (
  settledResults: PromiseSettledResult<Feature<Geometry, GeoJsonProperties>>[],
) => {
  const hydratedFeatures: Feature<Geometry>[] = [];

  for (const result of settledResults) {
    if (result.status === "fulfilled") {
      hydratedFeatures.push(result.value);
    }
  }

  return hydratedFeatures;
};

export const categorizeFeatures = (
  settledResults: PromiseSettledResult<Feature<Geometry, GeoJsonProperties> | Feature<Geometry, GeoJsonProperties>[]>[],
): {
  polygons: FeatureCollection<Polygon, GeoJsonProperties>;
  lineStrings: FeatureCollection<LineString, GeoJsonProperties>;
} => {
  const hydratedPolygonFeatures: Feature<Polygon, GeoJsonProperties>[] = [];
  const hydratedLineStringFeatures: Feature<LineString, GeoJsonProperties>[] = [];

  // Loop through settled results once
  for (const result of settledResults) {
    if (result.status === "fulfilled") {
      // if array we need to loop.
      const featureOrFeatures = result.value;
      if (Array.isArray(featureOrFeatures)) {
        for (const feature of featureOrFeatures) {
          if (feature.geometry.type === "Polygon") {
            hydratedPolygonFeatures.push(feature as Feature<Polygon, GeoJsonProperties>);
          } else if (feature.geometry.type === "LineString") {
            hydratedLineStringFeatures.push(feature as Feature<LineString, GeoJsonProperties>);
          }
        }
      } else {
        if (featureOrFeatures.geometry.type === "Polygon") {
          hydratedPolygonFeatures.push(featureOrFeatures as Feature<Polygon, GeoJsonProperties>);
        } else if (featureOrFeatures.geometry.type === "LineString") {
          hydratedLineStringFeatures.push(featureOrFeatures as Feature<LineString, GeoJsonProperties>);
        }
      }
    }
  }

  // Create feature collections
  const polygonsFeatureCollection: FeatureCollection<Polygon, GeoJsonProperties> = {
    type: "FeatureCollection",
    features: hydratedPolygonFeatures,
  };
  const lineStringFeatureCollection: FeatureCollection<LineString, GeoJsonProperties> = {
    type: "FeatureCollection",
    features: hydratedLineStringFeatures,
  };

  return {
    polygons: polygonsFeatureCollection,
    lineStrings: lineStringFeatureCollection,
  };
};

const modeFunction = (list: number[]): number => {
  const frequencyMap: { [key: number]: number } = {};

  // Count the frequency of each number in the list
  for (const num of list) {
    if (frequencyMap[num]) {
      frequencyMap[num]++;
    } else {
      frequencyMap[num] = 1;
    }
  }

  let mode = list[0];
  let maxCount = 0;

  // Find the number with the highest frequency
  for (const num in frequencyMap) {
    if (frequencyMap[num] > maxCount) {
      maxCount = frequencyMap[num];
      mode = Number(num);
    }
  }

  return mode;
};
const medianFunction = (list: number[]): number => {
  // Sort the list to find the median
  const sortedList = [...list].sort((a, b) => a - b);
  const middle = Math.floor(sortedList.length / 2);

  // If the list has an odd length, return the middle element
  // Otherwise, return the average of the two middle elements
  if (sortedList.length % 2 === 0) {
    return (sortedList[middle - 1] + sortedList[middle]) / 2;
  }
  return sortedList[middle];
};
const meanFunction = (list: number[]): number => {
  return list.reduce((sum, value) => sum + value, 0) / list.length;
};

export const integralFunction = (list: number[]): number => {
  // Simple trapezoidal rule with uniform spacing (dx = 1)
  // with lines we have always the same distance (point list)
  let sum = 0;
  for (let i = 0; i < list.length - 1; i++) {
    sum += (list[i] + list[i + 1]) / 2;
  }
  return sum;
};

export const aggregateOfListForLine = (aggregate: Aggregate, list: number[]): number | null => {
  if (list.length === 0) {
    return null;
  }
  switch (aggregate) {
    case Aggregate.min: {
      return Math.min(...list);
    }
    case Aggregate.max: {
      return Math.max(...list);
    }
    case Aggregate.mean: {
      return meanFunction(list);
    }
    case Aggregate.median: {
      return medianFunction(list);
    }
    case Aggregate.mode: {
      return modeFunction(list);
    }
    case Aggregate.integral: {
      // fallback value. Integral does not make too much sense on lines.
      return meanFunction(list);
    }
    default:
      console.warn("No aggregate set");
      return null;
  }
};

export const advancedThresholdValuePropertyName = (id: string): string => {
  return `value_${id}`;
};

export const roundThresholdValue = (value: number): number => {
  return Number.parseFloat(value.toFixed(4));
};
